import {Component, effect, inject, signal} from '@angular/core';
import {NgbDate, NgbDatepicker, NgbDatepickerModule, NgbDateStruct} from "@ng-bootstrap/ng-bootstrap";
import {Location, NgClass} from "@angular/common";
import {ReactiveFormsModule} from "@angular/forms";
import {GenericButtonComponent} from "src/app/presentation/common/generic-button/generic-button.component";
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {MapStore} from 'src/app/domain/map/map.store';
import {
  InternalNotificationsStore,
  InternalNotificationType
} from 'src/app/infrastructure/internal-notifications/internal-notifications.store';
import {MainViewStore} from 'src/app/presentation/main-view/main-view.store';
import {RssiService} from 'src/app/domain/samples/rssi/rssi.service';
import {FarmStore} from 'src/app/domain/assets/farm/farm.store';
import {
  SignalStrengthAnalysisStore
} from 'src/app/presentation/main-view/signal-strength-analysis/signal-strength-analysis.store';
import {GatewayStore} from 'src/app/domain/devices/gateway/gateway.store';
import {LoRaGateway} from 'src/app/infrastructure/moovement-api/models/asset';
import {MapStateService} from 'src/app/presentation/main-view/map-container/map-container-component';
import {twentyFourHours} from 'src/app/presentation/common/globals';

@Component({
  selector: 'app-signal-strength',
  imports: [
    GenericButtonComponent,
    NgbDatepicker,
    NgbDatepickerModule,
    ReactiveFormsModule,
    NgClass
  ],
  templateUrl: './signal-strength-analysis.component.html',
  styleUrl: './signal-strength-analysis.component.css'
})
export class SignalStrengthAnalysisComponent {

  overlayShown: boolean = false;
  hoveredDate: NgbDate | null = null;
  fromDate?: NgbDate
  toDate?: NgbDate | null

  loading: boolean = true

  selectedGateway = signal<LoRaGateway | undefined>(undefined);
  gateways = signal<LoRaGateway[]>([]);

  signalStrengthAnalysisStore = inject(SignalStrengthAnalysisStore);

  private location = inject(Location);
  private mainViewStore = inject(MainViewStore);

  private farmStore = inject(FarmStore);
  private gatewayStore = inject(GatewayStore);
  private mapStore = inject(MapStore);
  private internalNotificationsStore = inject(InternalNotificationsStore);

  private currentDate: Date = new Date();
  today: NgbDateStruct = {
    year: this.currentDate.getFullYear(), month: this.currentDate.getMonth() + 1, day: this.currentDate.getDate()
  };

  constructor(
    private dataSubscriptionService: DataSubscriptionService,
    private rssiService: RssiService,
    private mapStateService: MapStateService,
    protected viewportService: ViewportService
  ) {
    this.loading = false;

    effect(() => {
      const gateways = this.gatewayStore.entities();
      const gatewayIds = this.signalStrengthAnalysisStore.gatewayIds();

      this.gateways.set(gateways.filter(gateway => gatewayIds.has(gateway.id)));
    });
  }

  back() {
    this.location.back();
    this.reset();
  }

  close() {
    this.mainViewStore.hideChildComponent();
  }

  showOverlay() {
    const farmId = this.farmStore.farm()?.id;
    if (!farmId || this.overlayShown || !this.fromDate) return;

    this.loading = true;
    this.signalStrengthAnalysisStore.reset();

    const fromDate = new Date(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day);
    const toDate = this.toDate ?
      new Date(this.toDate.year, this.toDate.month - 1, this.toDate.day + 1) :
      new Date(this.fromDate.year, this.fromDate.month - 1, this.fromDate.day + 1);

    this.rssiService.locations(farmId, fromDate, toDate).subscribe({
      next: (locations) => {
        if (locations.length > 0) {
          this.signalStrengthAnalysisStore.setLocations(locations);

          this.mapStore.showSignalStrengthAnalysisLayer();
          this.mapStore.showGatewayLayer();

          this.mapStore.hideWaterPointLayer();
          this.mapStore.hideAnimalLocationLayer();
          this.mapStore.hidePrecipitationSamplePointLayer();
          this.mapStore.hideProximitySamplePointLayer();

          this.mapStore.hideHistoricAnimalLocationLayer();
          this.mapStore.hideGrazingPatternLayer();

          this.overlayShown = true
        } else {
          this.internalNotificationsStore.addNotification("There's no signal-strength data available for the selected period.", InternalNotificationType.INFO, false);
          this.mapStore.resetLayers();
        }
        this.loading = false
      },
      error: () => {
        this.internalNotificationsStore.addNotification("An unexpected error occured while retrieving signal-strength data.", InternalNotificationType.ERROR, false);
        this.loading = false;
      }
    });
  }

  onDateSelection(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
      this.fromDate = date;
    } else if (this.fromDate && !this.toDate && this.isValid(date)) {
      this.toDate = date;
    if (this.overlayShown) this.reset();
    } else {
      this.fromDate = date;
      this.toDate = null;
    }
  }

  isValid(date: NgbDate) {
    if (date.before(this.fromDate!)) return false;

    const from = new Date(this.fromDate!.year, this.fromDate!.month - 1, this.fromDate!.day);
    const to = new Date(date.year, date.month - 1, date.day + 1);
    const deltaDays = (to.getTime() - from.getTime()) / twentyFourHours;

    return deltaDays <= 8;
  }

  isInHoveredRange(date: NgbDate) {
    return this.fromDate && !this.toDate && this.hoveredDate && date.after(this.fromDate) && date.before(this.hoveredDate);
  }

  isInSelectedRange(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isValidRange() {
    return (this.fromDate && this.toDate) || (this.hoveredDate && this.isValid(this.hoveredDate));
  }

  isStartOrEndOfRange(date: NgbDate) {
    return date.equals(this.fromDate) ||
      date.equals(this.toDate);
  }

  reset() {
    this.mapStore.resetLayers();
    this.signalStrengthAnalysisStore.reset();
    this.overlayShown = false
  }

  onGatewayFilterSelection(gateway?: LoRaGateway) {
    this.selectedGateway.set(gateway);
    this.signalStrengthAnalysisStore.setVisibleGateway(gateway?.id);
  }

  onNavigateToSelectedGateway() {
    const gateway = this.selectedGateway();
    if (!gateway || !gateway.location || !gateway.location.coords) return;

    const coords = gateway.location.coords;
    this.mapStateService.centerPan(coords[0], coords[1]);
  }
}
